/**
 * Dialog for completing a consultation and adding notes/action items
 */

import { useState } from 'react';
import { CheckCircle2, Plus, Trash2, Calendar } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useConsultationMutations, useActionItemMutations, Consultation } from '@/hooks/use-consultations';
import { cn } from '@/lib/utils';

interface ActionItemInput {
  description: string;
  due_date?: string;
  assigned_to: 'trainer' | 'client';
}

interface CompleteConsultationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consultation: Consultation;
}

export function CompleteConsultationDialog({
  open,
  onOpenChange,
  consultation,
}: CompleteConsultationDialogProps) {
  const [notes, setNotes] = useState(consultation.notes || '');
  const [outcomeSummary, setOutcomeSummary] = useState('');
  const [nextConsultationDate, setNextConsultationDate] = useState<Date | undefined>(
    addMonths(new Date(), 1)
  );
  const [actionItems, setActionItems] = useState<ActionItemInput[]>([]);
  const [newActionItem, setNewActionItem] = useState('');
  const [newActionDueDate, setNewActionDueDate] = useState<Date>();
  const [newActionAssignee, setNewActionAssignee] = useState<'trainer' | 'client'>('client');

  const { completeConsultation } = useConsultationMutations();
  const { createActionItem } = useActionItemMutations();

  const handleAddActionItem = () => {
    if (!newActionItem.trim()) return;

    setActionItems([
      ...actionItems,
      {
        description: newActionItem.trim(),
        due_date: newActionDueDate?.toISOString().split('T')[0],
        assigned_to: newActionAssignee,
      },
    ]);
    setNewActionItem('');
    setNewActionDueDate(undefined);
  };

  const handleRemoveActionItem = (index: number) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Complete the consultation
    await completeConsultation.mutateAsync({
      id: consultation.id,
      notes,
      outcome_summary: outcomeSummary,
      next_consultation_date: nextConsultationDate?.toISOString().split('T')[0],
    });

    // Create action items
    for (const item of actionItems) {
      await createActionItem.mutateAsync({
        consultation_id: consultation.id,
        client_id: consultation.client_id,
        description: item.description,
        due_date: item.due_date,
        assigned_to: item.assigned_to,
      });
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            Complete Consultation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Notes */}
          <div className="space-y-2">
            <Label>Session Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed? Key takeaways..."
              rows={4}
            />
          </div>

          {/* Outcome Summary */}
          <div className="space-y-2">
            <Label>Outcome Summary</Label>
            <Textarea
              value={outcomeSummary}
              onChange={(e) => setOutcomeSummary(e.target.value)}
              placeholder="Brief summary of outcomes and decisions made..."
              rows={2}
            />
          </div>

          {/* Action Items */}
          <div className="space-y-3">
            <Label>Action Items</Label>

            {/* Existing items */}
            {actionItems.length > 0 && (
              <div className="space-y-2">
                {actionItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-2 bg-muted rounded-md"
                  >
                    <div className="flex-1">
                      <p className="text-sm">{item.description}</p>
                      <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                        <span className={cn(
                          "px-1.5 py-0.5 rounded",
                          item.assigned_to === 'trainer' ? 'bg-info/15 text-info' : 'bg-success/15 text-success'
                        )}>
                          {item.assigned_to === 'trainer' ? 'You' : 'Client'}
                        </span>
                        {item.due_date && (
                          <span>Due: {format(new Date(item.due_date), 'MMM d')}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveActionItem(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new item */}
            <div className="space-y-2 p-3 border rounded-md">
              <Input
                value={newActionItem}
                onChange={(e) => setNewActionItem(e.target.value)}
                placeholder="Add an action item..."
                onKeyDown={(e) => e.key === 'Enter' && handleAddActionItem()}
              />
              <div className="flex gap-2">
                <Select
                  value={newActionAssignee}
                  onValueChange={(v) => setNewActionAssignee(v as 'trainer' | 'client')}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="trainer">You</SelectItem>
                  </SelectContent>
                </Select>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-start">
                      <Calendar className="mr-2 h-4 w-4" />
                      {newActionDueDate ? format(newActionDueDate, 'MMM d') : 'Due date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={newActionDueDate}
                      onSelect={setNewActionDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleAddActionItem}
                  disabled={!newActionItem.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Next Consultation */}
          <div className="space-y-2">
            <Label>Schedule Next Consultation</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !nextConsultationDate && 'text-muted-foreground'
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {nextConsultationDate
                    ? format(nextConsultationDate, 'PPP')
                    : 'Select date for next check-in'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={nextConsultationDate}
                  onSelect={setNextConsultationDate}
                  initialFocus
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={completeConsultation.isPending}
            className="bg-success hover:bg-success/90"
          >
            {completeConsultation.isPending ? 'Saving...' : 'Complete Consultation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
